import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Close, PhotoCamera, Delete } from '@mui/icons-material';
import { FC, useState, useEffect, useRef } from 'react';
import { 
  useJobseekerUpdateProfileMutation,
  useUploadProfilePictureMutation,
  useDeleteProfilePictureMutation,
  useJobseekerProfileQuery,
} from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { JobseekerProfileResponseDto } from '@libs/dto';
import { ApiError } from '@libs/types';

interface JobseekerProfileModalProps {
  open: boolean;
  onClose: () => void;
  profile?: JobseekerProfileResponseDto;
}

export const JobseekerProfileModal: FC<JobseekerProfileModalProps> = ({ open, onClose, profile }) => {
  const { session } = useAuth();
  const [updateProfile, { isLoading: isUpdating }] = useJobseekerUpdateProfileMutation();
  const [uploadProfilePicture, { isLoading: isUploading }] = useUploadProfilePictureMutation();
  const [deleteProfilePicture, { isLoading: isDeleting }] = useDeleteProfilePictureMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get fresh profile data to ensure we have the latest profile picture status
  const { data: currentProfile } = useJobseekerProfileQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  // Use currentProfile if available, otherwise fallback to prop
  const activeProfile = currentProfile || profile;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  const [errors, setErrors] = useState<ApiError[]>([]);

  useEffect(() => {
    if (activeProfile) {
      setFormData({
        firstName: activeProfile.firstName || '',
        lastName: activeProfile.lastName || '',
        phoneNumber: activeProfile.phoneNumber || '',
      });
    }
    setErrors([]);
  }, [activeProfile, open]);



  const getFieldError = (fieldName: string): string => {
    const fieldError = errors.find(error => error.property === fieldName);
    return fieldError?.errors?.[0] || '';
  };

  const hasFieldError = (fieldName: string): boolean => {
    return !!getFieldError(fieldName);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    if (hasFieldError(field)) {
      setErrors(prev => prev.filter(error => error.property !== field));
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && session?.profile?.id) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        await uploadProfilePicture({
          formData,
          jobseekerId: session.profile.id,
        }).unwrap();
        
        // Image will update automatically when activeProfile updates
      } catch (error) {
        console.error('Failed to upload profile picture:', error);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!session?.profile?.id) return;

    try {
      await deleteProfilePicture(session.profile.id).unwrap();
      // Image will update automatically when activeProfile updates
    } catch (error) {
      console.error('Failed to delete profile picture:', error);
    }
  };

  const handleSubmit = async () => {
    if (!session?.profile?.id) return;

    setErrors([]);

    try {
      await updateProfile({
        id: session.profile.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
      }).unwrap();

      onClose();
    } catch (error: any) {
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      }
    }
  };

  const isFormValid = formData.firstName && formData.lastName;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Edit Profile</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Profile Picture Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                fontSize: '3rem',
              }}
              src={activeProfile?.profilePictureUrl || undefined}
            >
              {isUploading ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  {activeProfile?.firstName?.[0]}
                  {activeProfile?.lastName?.[0]}
                </>
              )}
            </Avatar>
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={handleProfilePictureClick}
                disabled={isUploading}
                sx={{ borderRadius: 2 }}
              >
                {activeProfile?.profilePictureUrl ? 'Change Photo' : 'Upload Photo'}
              </Button>
              
              {activeProfile?.profilePictureUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteProfilePicture}
                  disabled={isDeleting}
                  sx={{ borderRadius: 2 }}
                >
                  {isDeleting ? 'Removing...' : 'Remove'}
                </Button>
              )}
            </Stack>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </Box>

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              fullWidth
              required
              error={hasFieldError('firstName')}
              helperText={getFieldError('firstName')}
              sx={{ borderRadius: 4 }}
              InputProps={{
                sx: { borderRadius: 4 },
              }}
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              fullWidth
              required
              error={hasFieldError('lastName')}
              helperText={getFieldError('lastName')}
              sx={{ borderRadius: 4 }}
              InputProps={{
                sx: { borderRadius: 4 },
              }}
            />
          </Box>
          <TextField
            label="Phone Number"
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            fullWidth
            placeholder="e.g., (555) 123-4567"
            error={hasFieldError('phoneNumber')}
            helperText={getFieldError('phoneNumber')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isUpdating}
          sx={{ borderRadius: 2 }}
        >
          {isUpdating ? 'Updating...' : 'Update Profile'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 